export type ApiModule = "hda" | "bookings" | "visitors" | "delays";
export type RealtimeModule = "bookings" | "visitors";

interface ModuleEnvConfig {
  urlEnvVar: string;
  apiKeyEnvVar: string;
}

export interface ModuleApiConfig {
  baseUrl: string;
  apiKey: string;
}

const MODULE_ENV_CONFIG: Record<ApiModule, ModuleEnvConfig> = {
  hda: {
    urlEnvVar: "VUE_APP_API_HDA_URL",
    apiKeyEnvVar: "VUE_APP_API_HDA_API_KEY",
  },
  bookings: {
    urlEnvVar: "VUE_APP_API_BOOKINGS_URL",
    apiKeyEnvVar: "VUE_APP_API_BOOKINGS_API_KEY",
  },
  visitors: {
    urlEnvVar: "VUE_APP_API_VISITORS_URL",
    apiKeyEnvVar: "VUE_APP_API_VISITORS_API_KEY",
  },
  delays: {
    urlEnvVar: "VUE_APP_API_DELAYS_URL",
    apiKeyEnvVar: "VUE_APP_API_DELAYS_API_KEY",
  },
};

/** Removes trailing slashes in O(n) without regex (avoids ReDoS on untrusted env values). */
export function trimTrailingSlashes(url: string): string {
  let end = url.length;
  while (end > 0 && url.codePointAt(end - 1) === 47) {
    end -= 1;
  }
  return end === url.length ? url : url.slice(0, end);
}

function readRequiredEnv(envVar: string, moduleName: ApiModule): string {
  const value = process.env[envVar]?.trim();
  if (value) {
    return value;
  }

  throw new Error(
    `Missing required API configuration for module ${moduleName}: ${envVar}`
  );
}

export function getModuleApiConfig(moduleName: ApiModule): ModuleApiConfig {
  const moduleConfig = MODULE_ENV_CONFIG[moduleName];
  return {
    baseUrl: trimTrailingSlashes(readRequiredEnv(moduleConfig.urlEnvVar, moduleName)),
    apiKey: readRequiredEnv(moduleConfig.apiKeyEnvVar, moduleName),
  };
}

export interface SockJsClientOptions {
  transportOptions?: Record<string, { headers?: Record<string, string> }>;
}

/** STOMP CONNECT + SockJS transports: JWT only (backend does not validate x-api-key on /ws/**). */
export function buildSockJsAuthHeaders(accessToken: string): Record<string, string> {
  return {
    Authorization: `Bearer ${accessToken}`,
  };
}

export function buildSockJsClientOptions(accessToken: string): SockJsClientOptions {
  const authHeaders = buildSockJsAuthHeaders(accessToken);
  return {
    transportOptions: {
      "xhr-streaming": { headers: authHeaders },
      "xhr-polling": { headers: authHeaders },
      websocket: { headers: authHeaders },
    },
  };
}

export function getModuleSockJsUrl(endpoint: RealtimeModule): string {
  return `${getModuleApiConfig(endpoint).baseUrl}/ws/${endpoint}`;
}
